import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import pb from '../lib/pocketbase';
import styles from './ProjectDetail.module.css';
import Button from '../components/common/Button/Button';
import ProjectCard from '../components/realisations/ProjectCard/ProjectCard';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import ActionCTA from '../components/common/ActionCTA/ActionCTA';

function ProjectDetail() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [similarProjects, setSimilarProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    const fetchProject = async () => {
      try {
        const record = await pb.collection('projects').getFirstListItem(`slug="${slug}"`, {
          signal: controller.signal,
        });
        setProject(record);
        fetchSimilarProjects(record);
      } catch (err) {
        if (!err.isAbort) {
          setError('Projet non trouvé ou une erreur est survenue.');
          console.error(err);
        }
      } finally {
        setLoading(false);
      }
    };

    const fetchSimilarProjects = async (currentProject) => {
      if (!currentProject) return;

      const filter = `id != "${currentProject.id}" && category = "${currentProject.category}"`;

      try {
        let records = await pb.collection('projects').getList(1, 3, {
            filter: filter,
            signal: controller.signal,
        });

        // If no projects in the same category, fetch other recent ones
        if (records.items.length === 0) {
            records = await pb.collection('projects').getList(1, 3, {
                filter: `id != "${currentProject.id}"`,
                signal: controller.signal,
            });
        }

        setSimilarProjects(records.items);
      } catch (err) {
        if (!err.isAbort) {
            console.error("Failed to fetch similar projects", err);
        }
      }
    }

    fetchProject();

    return () => {
      controller.abort();
    };
  }, [slug]);

  if (loading) {
    return <div className="container"><p>Chargement du projet...</p></div>;
  }

  if (error) {
    return <div className="container"><p className={styles.error}>{error}</p></div>;
  }

  if (!project) {
    return <div className="container"><p>Aucun projet trouvé.</p></div>;
  }

  const coverImageUrl = project.cover_image ? pb.files.getURL(project, project.cover_image) : '/images/placeholder.png';
  const galleryThumbnails = project.gallery ? project.gallery.map(image => pb.files.getURL(project, image, {'thumb': '300x400'})) : [];
  const galleryFullSize = project.gallery ? project.gallery.map(image => ({ src: pb.files.getURL(project, image) })) : [];

  return (
    <div className={styles.projectDetailContainer}>
      <div className={styles.hero} style={{ backgroundImage: `url(${coverImageUrl})` }}>
        <div className={styles.heroOverlay}>
          <div className="container">
            <h1 className={styles.projectTitle}>{project.title}</h1>
            {project.category && <span className={styles.category}>{project.category}</span>}
          </div>
        </div>
      </div>

      <div className="container section">
        <div className={styles.contentLayout}>
          <div className={styles.mainContent}>
            <h2>Description du Projet</h2>
            <div className={styles.description} dangerouslySetInnerHTML={{ __html: project.description }}></div>
          </div>
          <aside className={styles.sidebar}>
            <h3>Détails</h3>
            <ul>
              {project.project_date && 
                <li><strong>Date :</strong> {new Date(project.project_date).toLocaleDateString('fr-FR')}</li>
              }
            </ul>
          </aside>
        </div>

        {galleryThumbnails.length > 0 && (
          <>
            <div className={styles.gallerySection}>
              <h2>Galerie d'images</h2>
              <div className={styles.galleryGrid}>
                {galleryThumbnails.map((url, index) => (
                  <div 
                    key={index} 
                    className={styles.galleryImageWrapper}
                    onClick={() => {
                      setLightboxIndex(index);
                      setLightboxOpen(true);
                    }}
                  >
                    <img src={url} alt={`Galerie du projet ${project.title} - ${index + 1}`} />
                  </div>
                ))}
              </div>
            </div>
            <Lightbox
                open={lightboxOpen}
                close={() => setLightboxOpen(false)}
                slides={galleryFullSize}
                index={lightboxIndex}
            />
          </>
        )}

        {similarProjects.length > 0 && (
          <div className={styles.similarProjectsSection}>
            <h2>Projets Similaires</h2>
            <div className={styles.similarProjectsGrid}>
              {similarProjects.map((p) => {
                const imageUrl = p.cover_image ? pb.files.getURL(p, p.cover_image, {'thumb': '400x400'}) : '/images/placeholder.png';
                return (
                  <ProjectCard 
                    key={p.id}
                    title={p.title}
                    description={p.description.replace(/<[^>]+>/g, '').substring(0, 100) + '...'}
                    imageSrc={imageUrl}
                    detailsLink={`/realisations/${p.slug}`}
                  />
                )
              })}
            </div>
          </div>
        )}

        <div className={styles.backLinkContainer}>
            <Button to="/realisations" variant="secondary">Retour aux réalisations</Button>
        </div>

      </div>
      <ActionCTA 
        title="Prêt à donner vie à votre projet ?"
        text="Contactez-nous pour une consultation gratuite et sans engagement. Ensemble, nous pouvons construire la maison de vos rêves."
        buttonText="Prendre rendez-vous"
      />
    </div>
  );
}

export default ProjectDetail;
